import { getWindow, openLinkBlank, MAX_LINK_LENGTH } from 'src/app/utils/web-api';
import { TonConnectUIError } from 'src/errors';
import { logDebug, logError } from 'src/app/utils/log';
import { setLastOpenedLink } from 'src/app/state/modals-state';
import { removeEmbeddedRequestFromUniversalLink } from 'src/app/utils/url-strategy-helpers';

type TmaPlatform = 'android' | 'ios' | 'macos' | 'tdesktop' | 'weba' | 'web' | 'unknown';

type TelegramWebviewProxy = {
    postEvent(eventType: string, eventData: string): void;
};

type TelegramWebview = unknown;

declare global {
    interface External {
        notify: (message: string) => void;
    }

    interface Window {
        TelegramWebviewProxy?: TelegramWebviewProxy;
        TelegramWebview?: TelegramWebview;
        Telegram?: {
            WebApp?: {
                platform?: TmaPlatform;
                version?: string;
            };
        };
    }
}

let initParams: Record<string, string> = {};
try {
    let locationHash = location.hash.toString();
    initParams = urlParseHashParams(locationHash);
} catch (e) {}

function sessionStorageSet(key: string, value: string) {
    try {
        const window = getWindow();
        return window?.sessionStorage?.setItem(key, value);
    } catch (e) {}
}

function sessionStorageGet(key: string) {
    try {
        const window = getWindow();
        return JSON.parse(window?.sessionStorage?.getItem?.(key)!);
    } catch (e) {}
    return null;
}

function updateFromStoredParams(key: string) {
    try {
        const storedParams = sessionStorageGet(key);
        if (storedParams) {
            for (const key in storedParams) {
                if (typeof initParams[key] === 'undefined') {
                    initParams[key] = storedParams[key];
                }
            }
        }
    } catch {}
}

function updateFromStoredRawParams(key: string) {
    try {
        const window = getWindow();
        let raw = window?.sessionStorage?.getItem?.(key);
        if (!raw) {
            return;
        }

        try {
            const parsedJsonRaw = JSON.parse(raw);
            if (typeof parsedJsonRaw === 'string') {
                raw = parsedJsonRaw;
            } else {
                return;
            }
        } catch (e) {}

        const storedParams = urlParseQueryString(raw);
        for (const paramKey in storedParams) {
            const value = storedParams[paramKey];
            if (value != null && typeof initParams[paramKey] === 'undefined') {
                initParams[paramKey] = value;
            }
        }
    } catch {}
}

const LAUNCH_PARAMS_STORAGE_KEY = 'ton-connect-session_storage_launchParams';

updateFromStoredRawParams('tapps/launchParams');
updateFromStoredParams('__telegram__initParams');
updateFromStoredParams(LAUNCH_PARAMS_STORAGE_KEY);

try {
    if (Object.entries(initParams).length > 0) {
        sessionStorageSet(LAUNCH_PARAMS_STORAGE_KEY, JSON.stringify(initParams));
    }
} catch (e) {}

let tmaPlatform: TmaPlatform = 'unknown';
if (initParams?.tgWebAppPlatform) {
    tmaPlatform = (initParams.tgWebAppPlatform as TmaPlatform) ?? 'unknown';
}
if (tmaPlatform === 'unknown') {
    const window = getWindow();
    tmaPlatform = window?.Telegram?.WebApp?.platform ?? 'unknown';
}

let webAppVersion = '6.0';
if (initParams?.tgWebAppVersion) {
    webAppVersion = initParams.tgWebAppVersion;
}
if (!webAppVersion) {
    const window = getWindow();
    webAppVersion = window?.Telegram?.WebApp?.version ?? '6.0';
}

const initDataRaw = initParams?.tgWebAppData;

type TelegramUser = {
    id: number;
    isPremium: boolean;
};

let telegramUser: TelegramUser | undefined = undefined;

try {
    if (initDataRaw) {
        let initData = urlParseQueryString(initDataRaw);
        let userRaw = initData.user;
        if (userRaw) {
            let user = JSON.parse(userRaw);
            if (typeof user.id === 'number' && typeof user.is_premium === 'boolean') {
                telegramUser = {
                    id: user.id,
                    isPremium: user.is_premium
                };
            }
        }
    }
} catch (e) {}

/**
 * Returns telegram user parsed from telegram initData.
 */
export function getTgUser(): TelegramUser | undefined {
    return telegramUser;
}

/**
 * Returns the detected TMA platform string.
 */
export function getTmaPlatform(): TmaPlatform {
    return tmaPlatform;
}

/**
 * Returns the detected TMA WebApp version, or null when not in a Mini App.
 */
export function getWebAppVersion(): string | null {
    return isInTMA() ? webAppVersion : null;
}

/**
 * Returns true if the app is running in TMA on the specified platform.
 * @param platforms
 */
export function isTmaPlatform(...platforms: TmaPlatform[]): boolean {
    return platforms.includes(tmaPlatform);
}

/**
 * Returns true if the app is running in TMA.
 */
export function isInTMA(): boolean {
    return tmaPlatform !== 'unknown' || !!getWindow()?.TelegramWebviewProxy;
}

/**
 * Returns true if the app is running in the Telegram browser.
 */
export function isInTelegramBrowser(): boolean {
    const isTelegramWebview = !!getWindow()?.TelegramWebview;

    return (isInTMA() || isTelegramWebview) && tmaPlatform === 'unknown';
}

/**
 * Expand the app window.
 */
export function sendExpand(): void {
    postEvent('web_app_expand', {});
}

/**
 * Opens link in TMA or in new tab and returns a function that closes the tab.
 * @param link The link to open.
 * @param fallback The function to call if the link can't be opened in TMA.
 */
export function sendOpenTelegramLink(link: string, fallback?: () => void): void {
    if (link.length > MAX_LINK_LENGTH) {
        link = removeEmbeddedRequestFromUniversalLink(link);
    }
    const url = new URL(link);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        if (fallback) {
            return fallback();
        }
        throw new TonConnectUIError(`Url protocol is not supported: ${url}`);
    }
    if (url.hostname !== 't.me') {
        if (fallback) {
            return fallback();
        }
        throw new TonConnectUIError(`Url host is not supported: ${url}`);
    }

    const pathFull = url.pathname + url.search;

    if (isIframe() || versionAtLeast('6.1')) {
        // TODO: should be extracted to upper layer
        setLastOpenedLink({ link: pathFull, type: 'tg_link' });
        postEvent('web_app_open_tg_link', { path_full: pathFull });
    } else {
        openLinkBlank('https://t.me' + pathFull);
    }
}

function isIframe(): boolean {
    try {
        const window = getWindow();
        if (!window) {
            return false;
        }
        return window.parent != null && window !== window.parent;
    } catch (e) {
        return false;
    }
}

function postEvent(eventType: 'web_app_open_tg_link', eventData: { path_full: string }): void;
function postEvent(eventType: 'web_app_expand', eventData: {}): void;
function postEvent(eventType: string, eventData: object): void {
    try {
        const window = getWindow();
        if (!window) {
            throw new TonConnectUIError(`Can't post event to parent window: window is not defined`);
        }

        if (window.TelegramWebviewProxy !== undefined) {
            logDebug('postEvent', eventType, eventData);
            window.TelegramWebviewProxy.postEvent(eventType, JSON.stringify(eventData));
        } else if (window.external && 'notify' in window.external) {
            logDebug('postEvent', eventType, eventData);
            window.external.notify(JSON.stringify({ eventType: eventType, eventData: eventData }));
        } else if (isIframe()) {
            const trustedTarget = '*';
            const message = JSON.stringify({ eventType: eventType, eventData: eventData });
            logDebug('postEvent', eventType, eventData);
            window.parent.postMessage(message, trustedTarget);
        } else {
            throw new TonConnectUIError(`Can't post event to TMA`);
        }
    } catch (e) {
        logError(`Can't post event to parent window: ${e}`);
    }
}

function urlParseHashParams(locationHash: string): Record<string, string> {
    locationHash = locationHash.replace(/^#/, '');
    let params: Record<string, string> = {};
    if (!locationHash.length) {
        return params;
    }
    if (locationHash.indexOf('=') < 0 && locationHash.indexOf('?') < 0) {
        params._path = urlSafeDecode(locationHash);
        return params;
    }
    let qIndex = locationHash.indexOf('?');
    if (qIndex >= 0) {
        let pathParam = locationHash.substr(0, qIndex);
        params._path = urlSafeDecode(pathParam);
        locationHash = locationHash.substr(qIndex + 1);
    }
    let query_params = urlParseQueryString(locationHash);
    for (let k in query_params) {
        params[k] = query_params[k]!;
    }
    return params;
}

function urlSafeDecode(urlencoded: string): string {
    try {
        urlencoded = urlencoded.replace(/\+/g, '%20');
        return decodeURIComponent(urlencoded);
    } catch (e) {
        return urlencoded;
    }
}

function urlParseQueryString(queryString: string): Record<string, string | null> {
    let params: Record<string, string | null> = {};
    if (!queryString.length) {
        return params;
    }
    let queryStringParams = queryString.split('&');
    let i, param, paramName, paramValue;
    for (i = 0; i < queryStringParams.length; i++) {
        param = queryStringParams[i]!.split('=');
        paramName = urlSafeDecode(param[0]!);
        paramValue = param[1] == null ? null : urlSafeDecode(param[1]);
        params[paramName] = paramValue;
    }
    return params;
}

function versionCompare(v1: string | undefined, v2: string | undefined): 0 | 1 | -1 {
    if (typeof v1 !== 'string') v1 = '';
    if (typeof v2 !== 'string') v2 = '';
    let v1List = v1.replace(/^\s+|\s+$/g, '').split('.');
    let v2List = v2.replace(/^\s+|\s+$/g, '').split('.');
    let a: number, i, p1, p2;
    a = Math.max(v1List.length, v2List.length);
    for (i = 0; i < a; i++) {
        p1 = parseInt(v1List[i]!) || 0;
        p2 = parseInt(v2List[i]!) || 0;
        if (p1 === p2) continue;
        if (p1 > p2) return 1;
        return -1;
    }
    return 0;
}

function versionAtLeast(ver: string): boolean {
    return versionCompare(webAppVersion, ver) >= 0;
}
